import { localFetch } from '../../../../lib/local_fetch'
import { ICartDeliveryAddress } from '../shopify_client'
import { cartFragment } from "../storefront_client/fragments"
import { shopifyGql, ShopifyGqlUserErrors } from "../shopify_client/graphql"
import { ICart, IShippingAddress } from "../shopify_client/types"
import { shippingAdddressToCartDeliveryAddressInput } from './create_cart'
import { checkAddressSufficient } from '../shopify_client/address_utils'

interface IUpdateAttributesInput {
  email?: string
  shippingAddress?: IShippingAddress
  b2bQuantityThreshold: number
}

const shippingAddressEquals = (left: ICartDeliveryAddress, right: IShippingAddress): boolean => (
  (!left && !right) ||
  (!!left && !!right) &&
  left.firstName === right.firstName &&
  left.lastName === right.lastName &&
  left.company === right.company &&
  left.address1 === right.address1 &&
  left.address2 === right.address2 &&
  left.city === right.city &&
  left.provinceCode === right.province &&
  left.zip === right.zip &&
  left.countryCode === right.country &&
  left.phone === right.phone
)

export const updateCart = async ({
  cart: inputCart,
  attributes,
}: {
  cart: ICart,
  attributes: IUpdateAttributesInput,
}): Promise<ICart> => {
  let cart = inputCart

  const totalItems = cart.lines.edges.reduce((prev, edge) => edge.node.quantity + prev, 0)
  const isB2B = totalItems >= attributes.b2bQuantityThreshold
  const expectedNote = isB2B ? '' : 'B2C'

  if (cart.note !== expectedNote) {
    const cartNoteUpdateInput = {
      cartId: cart.id,
      note: expectedNote
    }

    const { data, errors }  = await shopifyGql<ICartNoteUpdateData>(
      cartNoteUpdateMutation,
      cartNoteUpdateInput
    )

    if (errors.length > 0) { throw new AggregateError(errors, 'updateCart.cartNoteUpdate') }

    cart = data.cartNoteUpdate.cart
    await localFetch('/cart', { method: 'PUT', body: JSON.stringify({ cart: { b2b: isB2B } }) })
  }

  if (cart.buyerIdentity.email !== attributes.email) {
    const cartBuyerIdentityInput = {
      cartId: cart.id,
      buyerIdentity: { email: attributes.email }
    }

    const { data, errors } = await shopifyGql<ICartBuyerIdentityUpdateData>(
      cartBuyerIdentityUpdateMutation,
      cartBuyerIdentityInput
    )

    if (errors.length > 0) { throw new AggregateError(errors, 'updateCart.cartBuyerIdentityUpdate') }

    cart = data.cartBuyerIdentityUpdate.cart
  }

  if (cart.delivery.addresses.length > 0) {
    const { id: deliveryAddressId, address: deliveryAddress } = cart.delivery.addresses[0]
    if (!shippingAddressEquals(deliveryAddress, attributes.shippingAddress)) {
      if (checkAddressSufficient(attributes.shippingAddress)) {
        const cartDeliveryAddressesUpdateInput = {
          cartId: cart.id,
          addresses: [{
            id: deliveryAddressId,
            selected: true,
            address: {
              deliveryAddress: shippingAdddressToCartDeliveryAddressInput(attributes.shippingAddress)
            }
          }]
        }

        const { data, errors } = await shopifyGql<ICartDeliveryAddressesUpdateData>(
          cartDeliveryAddressesUpdateMutation,
          cartDeliveryAddressesUpdateInput
        )

        if (errors.length > 0) { throw new AggregateError(errors, 'updateCart.cartDeliveryAddressUpdate') }

        cart = data.cartDeliveryAddressesUpdate.cart
      } else {
        const cartDeliveryAddressesRemoveInput = {
          cartId: cart.id,
          addressIds: [deliveryAddressId]
        }

        const { data, errors } = await shopifyGql<ICartDeliveryAddressesRemoveData>(
          cartDeliveryAddressesRemoveMutation,
          cartDeliveryAddressesRemoveInput
        )

        if (errors.length > 0) { throw new AggregateError(errors, 'updateCart.cartDeliveryAddressRemove') }

        cart = data.cartDeliveryAddressesRemove.cart
      }
    }
  }

  return cart
}

interface ICartNoteUpdateData {
  cartNoteUpdate: {
    cart: {
      __typename: 'Cart'
    } & ICart
  } & ShopifyGqlUserErrors
}

const cartNoteUpdateMutation = `
mutation cartNoteUpdate($cartId: ID!, $note: String!) {
  cartNoteUpdate(cartId: $cartId, note: $note) {
    cart {
      ...CartFragment
    }
    userErrors {
      field
      message
    }
  }
}
${cartFragment}
`

interface ICartBuyerIdentityUpdateData {
  cartBuyerIdentityUpdate: {
    cart: {
      __typename: 'Cart'
    } & ICart
  } & ShopifyGqlUserErrors
}

const cartBuyerIdentityUpdateMutation = `
mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
  cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
    cart {
      ...CartFragment
    }
    userErrors {
      field
      message
    }
  }
}
${cartFragment}
`

interface ICartDeliveryAddressesUpdateData {
  cartDeliveryAddressesUpdate: {
    cart: {
      __typename: 'Cart'
    } & ICart
  } & ShopifyGqlUserErrors
}

const cartDeliveryAddressesUpdateMutation = `
mutation cartDeliveryAddressesUpdate($cartId: ID!, $addresses: [CartSelectableAddressUpdateInput!]!) {
  cartDeliveryAddressesUpdate(cartId: $cartId, addresses: $addresses) {
    cart {
      ...CartFragment
    }
    userErrors {
      field
      message
    }
  }
}
${cartFragment}
`

interface ICartDeliveryAddressesRemoveData {
  cartDeliveryAddressesRemove: {
    cart: {
      __typename: 'Cart'
    } & ICart
  } & ShopifyGqlUserErrors
}

const cartDeliveryAddressesRemoveMutation = `
mutation cartDeliveryAddressesRemove($cartId: ID!, $addressIds: [ID!]!) {
  cartDeliveryAddressesRemove(cartId: $cartId, addressIds: $addressIds) {
    cart {
      ...CartFragment
    }
    userErrors {
      field
      message
    }
  }
}
${cartFragment}
`
